/**
 * BackupsDashboard — admin.optiprime.ai/backups
 * Inventory of Spark + OneDrive backup layers with animated topology map.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import './BackupsDashboard.css';

const STATUS_CLASS = {
  active: 'bk-status-active',
  pending: 'bk-status-pending',
  missing: 'bk-status-missing',
  unknown: 'bk-status-unknown',
  syncing: 'bk-status-syncing',
  warning: 'bk-status-warning',
};

const LAYER_TO_NODE = {
  'git-spark': 'git',
  'daily-tar-local': 'tar-local',
  'daily-tar-onedrive': 'od-daily',
  'incremental-onedrive': 'od-projects',
};

const NODE_SUMMARIES = {
  source: {
    title: 'Live codebase',
    summary: 'Primary working tree on DGX Spark — all projects live here (including Cornelius). Feeds git, daily tar (nodes/), and hourly OneDrive sync.',
    path: '/home/optiprime/projects',
    schedule: 'Continuous (live)',
    retention: 'Current state on disk',
  },
  git: {
    title: 'Git history (Spark)',
    summary: 'Local version control for the nodes monorepo. Commits stay on Spark (not GitHub).',
    path: '/home/optiprime/projects/nodes/.git',
    schedule: 'Manual commits + OptiGuard auto-fix',
    retention: 'Full commit history',
  },
  'incr-local': {
    title: 'Projects tree (Spark)',
    summary: 'Same live /projects tree on DGX Spark — rclone reads this path each hour and syncs changes to OneDrive.',
    path: '/home/optiprime/projects',
    schedule: 'Hourly rclone sync source',
    retention: 'Live files on Spark',
  },
  'tar-local': {
    title: 'Daily full tar (nodes/)',
    summary: 'Compressed snapshot of nodes/ only (not all of /projects). Excludes .git, node_modules, venv, and LLM models.',
    path: '/home/optiprime/backups/nodes/',
    schedule: 'Daily 1:00 AM',
    retention: '3 most recent locally',
  },
  'tar-archive': {
    title: 'Rotated archive',
    summary: 'Older daily tars moved here when the 3-day local rotation runs.',
    path: '/home/optiprime/backups/archive/',
    schedule: 'After each daily backup',
    retention: 'Historical tars + DB dumps',
  },
  'od-projects': {
    title: 'OneDrive incremental',
    summary: 'Mirror of /projects on Spark — only new/changed files uploaded each hour.',
    path: 'OneDrive → Backups/projects/',
    schedule: 'Hourly (cron)',
    retention: 'Cloud mirror of Spark tree',
  },
  'od-daily': {
    title: 'OneDrive daily tar',
    summary: 'Single off-box full snapshot (~15 GB). Old cloud tar deleted after new upload.',
    path: 'OneDrive → Backups/nodes-daily/',
    schedule: 'After daily tar (~1:10 AM)',
    retention: '1 file (latest only)',
  },
};

const POSITIONS = {
  source:        { x: 50,  y: 52 },
  git:           { x: 20,  y: 24 },
  'tar-local':   { x: 20,  y: 78 },
  'tar-archive': { x: 8,   y: 92 },
  'incr-local':  { x: 58,  y: 22 },
  'od-projects': { x: 84,  y: 22 },
  'od-daily':    { x: 84,  y: 76 },
};

/** Primary flow edges — always rendered on top with stronger stroke */
const PRIMARY_EDGES = new Set(['source->incr-local', 'incr-local->od-projects']);

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString('en-CA', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function formatLastEvent(layer) {
  if (!layer) return '—';
  const when = fmtDate(layer.lastEvent);
  if (when !== '—' && layer.lastEventDetail) {
    return `${when} — ${layer.lastEventDetail}`;
  }
  if (when !== '—') return when;
  return layer.lastEventDetail || '—';
}

function buildNodeTooltip(nodeId, layers) {
  const base = NODE_SUMMARIES[nodeId] || {};
  const layerEntry = Object.entries(LAYER_TO_NODE).find(([, nid]) => nid === nodeId);
  const layer = layerEntry ? layers?.find((l) => l.id === layerEntry[0]) : null;

  return {
    title: layer?.name || base.title || nodeId,
    summary: layer?.notes || base.summary || '',
    path: layer?.location || base.path || '',
    schedule: layer?.schedule || base.schedule || '',
    retention: layer?.retention || base.retention || '',
    status: layer?.status || null,
    size: layer?.sizeHuman || null,
    lastEvent: layer?.lastEvent || null,
    lastEventDetail: layer?.lastEventDetail || null,
    device: layer?.device || null,
  };
}

function BackupTopology({ topology, layers }) {
  const { nodes = [], edges = [] } = topology || {};
  const viewportRef = useRef(null);
  const [transform, setTransform] = useState({ x: 0, y: 0, scale: 1 });
  const [hovered, setHovered] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const panRef = useRef({ startX: 0, startY: 0, origX: 0, origY: 0 });

  const edgePath = (from, to) => {
    const a = POSITIONS[from];
    const b = POSITIONS[to];
    if (!a || !b) return '';
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1;
    const rFrom = from === 'source' ? 5.5 : 4.2;
    const rTo = to === 'source' ? 5.5 : 4.2;
    const x1 = a.x + (dx / len) * rFrom;
    const y1 = a.y + (dy / len) * rFrom;
    const x2 = b.x - (dx / len) * rTo;
    const y2 = b.y - (dy / len) * rTo;
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;
    return `M ${x1} ${y1} Q ${mx} ${my} ${x2} ${y2}`;
  };

  const renderEdge = (edge, i, layer = 'base') => {
    const key = `${edge.from}->${edge.to}`;
    const isPrimary = PRIMARY_EDGES.has(key);
    if (layer === 'base' && isPrimary) return null;
    if (layer === 'overlay' && !isPrimary) return null;
    const d = edgePath(edge.from, edge.to);
    if (!d) return null;
    return (
      <g key={`${layer}-${key}`}>
        <path
          d={d}
          className={isPrimary ? 'bk-topology-edge bk-topology-edge-primary' : 'bk-topology-edge'}
          fill="none"
          stroke={isPrimary ? '#3cfcf9' : 'url(#bk-edge-grad)'}
          strokeWidth={isPrimary ? '0.65' : '0.35'}
        />
        <circle r={isPrimary ? '0.7' : '0.55'} className="bk-packet" style={{ animationDelay: `${i * 0.7}s` }}>
          <animateMotion dur={`${3.5 + i * 0.4}s`} repeatCount="indefinite" path={d} />
        </circle>
      </g>
    );
  };

  const clampScale = (s) => Math.min(4, Math.max(0.5, s));

  const handleWheel = (e) => {
    e.preventDefault();
    const rect = viewportRef.current?.getBoundingClientRect();
    if (!rect) return;
    const factor = e.deltaY > 0 ? 0.92 : 1.08;
    const nextScale = clampScale(transform.scale * factor);
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    setTransform((t) => {
      const ratio = nextScale / t.scale;
      return {
        scale: nextScale,
        x: mx - (mx - t.x) * ratio,
        y: my - (my - t.y) * ratio,
      };
    });
  };

  const handlePanStart = (e) => {
    if (e.button !== 0 || e.target.closest('.bk-topology-node-group')) return;
    setIsPanning(true);
    panRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: transform.x,
      origY: transform.y,
    };
  };

  const handlePanMove = (e) => {
    if (!isPanning) return;
    const { startX, startY, origX, origY } = panRef.current;
    setTransform((t) => ({
      ...t,
      x: origX + (e.clientX - startX),
      y: origY + (e.clientY - startY),
    }));
  };

  const handlePanEnd = () => setIsPanning(false);

  const zoomBy = (factor) => {
    const rect = viewportRef.current?.getBoundingClientRect();
    const cx = rect ? rect.width / 2 : 0;
    const cy = rect ? rect.height / 2 : 0;
    setTransform((t) => {
      const nextScale = clampScale(t.scale * factor);
      const ratio = nextScale / t.scale;
      return {
        scale: nextScale,
        x: cx - (cx - t.x) * ratio,
        y: cy - (cy - t.y) * ratio,
      };
    });
  };

  const resetView = () => setTransform({ x: 0, y: 0, scale: 1 });

  const handleNodeEnter = (nodeId, e) => {
    const rect = viewportRef.current?.getBoundingClientRect();
    if (!rect) return;
    setHovered({
      nodeId,
      tip: buildNodeTooltip(nodeId, layers),
      x: e.clientX - rect.left + 12,
      y: e.clientY - rect.top + 12,
    });
  };

  const handleNodeMove = (nodeId, e) => {
    if (!hovered || hovered.nodeId !== nodeId) return;
    const rect = viewportRef.current?.getBoundingClientRect();
    if (!rect) return;
    setHovered((h) => ({
      ...h,
      x: e.clientX - rect.left + 12,
      y: e.clientY - rect.top + 12,
    }));
  };

  return (
    <div className="bk-topology-wrap">
      <div className="bk-topology-toolbar">
        <button type="button" onClick={() => zoomBy(1.2)} title="Zoom in">+</button>
        <button type="button" onClick={() => zoomBy(1 / 1.2)} title="Zoom out">−</button>
        <button type="button" onClick={resetView} title="Reset view">Reset</button>
        <span className="bk-topology-hint">Scroll to zoom · drag to pan · hover nodes for details</span>
        <span className="bk-topology-zoom-label">{Math.round(transform.scale * 100)}%</span>
      </div>

      <div
        ref={viewportRef}
        className={`bk-topology-viewport${isPanning ? ' is-panning' : ''}`}
        onWheel={handleWheel}
        onMouseDown={handlePanStart}
        onMouseMove={handlePanMove}
        onMouseUp={handlePanEnd}
        onMouseLeave={handlePanEnd}
      >
        <div className="bk-topology-scanlines" aria-hidden="true" />

        <div
          className="bk-topology-canvas"
          style={{
            transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
          }}
        >
          <svg className="bk-topology-svg" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet">
            <defs>
              <linearGradient id="bk-edge-grad" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#3cfcf9" stopOpacity="0.2" />
                <stop offset="50%" stopColor="#3cfcf9" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#818cf8" stopOpacity="0.3" />
              </linearGradient>
              <filter id="bk-glow">
                <feGaussianBlur stdDeviation="0.4" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {edges.map((edge, i) => renderEdge(edge, i, 'base'))}

            {nodes.map((node) => {
              const pos = POSITIONS[node.id];
              if (!pos) return null;
              const isHovered = hovered?.nodeId === node.id;
              return (
                <g
                  key={node.id}
                  transform={`translate(${pos.x}, ${pos.y})`}
                  filter="url(#bk-glow)"
                  className={`bk-topology-node-group${isHovered ? ' is-hovered' : ''}`}
                  onMouseDown={(e) => e.stopPropagation()}
                  onMouseEnter={(e) => { e.stopPropagation(); handleNodeEnter(node.id, e); }}
                  onMouseMove={(e) => { e.stopPropagation(); handleNodeMove(node.id, e); }}
                  onMouseLeave={() => setHovered((h) => (h?.nodeId === node.id ? null : h))}
                >
                  <circle r={node.type === 'source' ? 6 : 5} className="bk-node-hit" fill="transparent" />
                  <circle
                    r={node.type === 'source' ? 4.2 : 3.2}
                    className={`bk-topology-node bk-node-${node.type}`}
                  />
                  <text y="-4.5" className="bk-node-label">{node.label}</text>
                  <text y="-1.8" className="bk-node-sub">{node.sub}</text>
                </g>
              );
            })}

            {edges.map((edge, i) => renderEdge(edge, i, 'overlay'))}
          </svg>
        </div>

        {hovered && (
          <div
            className="bk-node-tooltip"
            style={{
              left: Math.min(hovered.x, (viewportRef.current?.clientWidth || 400) - 280),
              top: Math.min(hovered.y, (viewportRef.current?.clientHeight || 300) - 180),
            }}
          >
            <div className="bk-tooltip-head">
              <strong>{hovered.tip.title}</strong>
              {hovered.tip.status && (
                <span className={`bk-status-pill ${STATUS_CLASS[hovered.tip.status] || 'bk-status-unknown'}`}>
                  {hovered.tip.status}
                </span>
              )}
            </div>
            <p className="bk-tooltip-summary">{hovered.tip.summary}</p>
            {hovered.tip.device && <div className="bk-tooltip-row"><span>Device</span><span>{hovered.tip.device}</span></div>}
            {hovered.tip.path && <div className="bk-tooltip-row"><span>Path</span><code>{hovered.tip.path}</code></div>}
            {hovered.tip.schedule && <div className="bk-tooltip-row"><span>Schedule</span><span>{hovered.tip.schedule}</span></div>}
            {hovered.tip.retention && <div className="bk-tooltip-row"><span>Retention</span><span>{hovered.tip.retention}</span></div>}
            {hovered.tip.size && hovered.tip.size !== '—' && (
              <div className="bk-tooltip-row"><span>Size</span><span>{hovered.tip.size}</span></div>
            )}
            {(hovered.tip.lastEvent || hovered.tip.lastEventDetail) && (
              <div className="bk-tooltip-row"><span>Last event</span><span>{formatLastEvent(hovered.tip)}</span></div>
            )}
          </div>
        )}
      </div>

      <div className="bk-topology-legend">
        <span><i className="bk-legend-dot bk-node-spark" /> DGX Spark</span>
        <span><i className="bk-legend-dot bk-node-cloud" /> OneDrive</span>
        <span><i className="bk-legend-dot bk-node-source" /> Live source</span>
        <span className="bk-legend-pulse">● animated = data flow</span>
      </div>
    </div>
  );
}

function truncatePath(path, max = 56) {
  if (!path || path.length <= max) return path;
  const head = path.slice(0, 22);
  const tail = path.slice(-28);
  return `${head}…${tail}`;
}

function DataTable({ title, columns, rows, emptyText = 'No entries', className = '', compact = false, maxBodyHeight }) {
  return (
    <div className={`bk-table-card${className ? ` ${className}` : ''}${compact ? ' bk-table-card--compact' : ''}`}>
      <h3 className="bk-table-title">{title}</h3>
      {(!rows || rows.length === 0) ? (
        <p className="bk-empty">{emptyText}</p>
      ) : (
        <div
          className="bk-table-scroll"
          style={maxBodyHeight ? { maxHeight: maxBodyHeight, overflowY: 'auto' } : undefined}
        >
          <table className="bk-table">
            <thead>
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className={c.colClass || ''}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={`${row.path || row.name || 'row'}-${i}`}>
                  {columns.map((c) => (
                    <td key={c.key} className={[c.mono && 'bk-mono', c.colClass].filter(Boolean).join(' ') || undefined}>
                      {c.render ? c.render(row) : (row[c.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function BackupsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchInventory = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch('/api/backups/inventory');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (err) {
      setError(err.message || 'Failed to load backup inventory');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchInventory();
    const id = setInterval(fetchInventory, 120_000);
    return () => clearInterval(id);
  }, [fetchInventory]);

  if (loading && !data) {
    return <div className="bk-dash bk-loading">Loading backup inventory…</div>;
  }

  if (error && !data) {
    return (
      <div className="bk-dash bk-error">
        <p>Could not load backups: {error}</p>
        <button type="button" onClick={fetchInventory}>Retry</button>
      </div>
    );
  }

  const { summary, layers, tables, cronJobs, logs, topology, scripts, host } = data;

  return (
    <div className="bk-dash">
      <header className="bk-hero">
        <div className="bk-hero-grid" aria-hidden="true" />
        <div className="bk-hero-content">
          <p className="bk-eyebrow">DATA PROTECTION · {host?.name}</p>
          <h2 className="bk-hero-title">Backup Command Center</h2>
          <p className="bk-hero-sub">
            Spark-local git + daily tar + OneDrive incremental &amp; daily off-box snapshots
          </p>
          <div className="bk-kpi-row">
            <div className="bk-kpi">
              <span className="bk-kpi-val">{summary?.localBackupDirSize || '—'}</span>
              <span className="bk-kpi-lbl">Local backup disk</span>
            </div>
            <div className="bk-kpi">
              <span className="bk-kpi-val">{summary?.localDailyTarCount ?? '—'}</span>
              <span className="bk-kpi-lbl">Daily tars (local)</span>
            </div>
            <div className="bk-kpi">
              <span className="bk-kpi-val">{summary?.gitBranch || '—'}</span>
              <span className="bk-kpi-lbl">Git branch</span>
            </div>
            <div className="bk-kpi">
              <span className="bk-kpi-val">{summary?.oneDriveDailyTar?.name ? '✓' : '—'}</span>
              <span className="bk-kpi-lbl">OneDrive daily tar</span>
            </div>
          </div>
        </div>
        <button type="button" className="bk-refresh-btn" onClick={fetchInventory} disabled={loading}>
          {loading ? 'Refreshing…' : '↻ Refresh'}
        </button>
      </header>

      <section className="bk-section">
        <h3 className="bk-section-title">Backup flow map</h3>
        <p className="bk-section-desc">
          Animated paths show how data moves from the live codebase on Spark to local archives and OneDrive.
        </p>
        <BackupTopology topology={topology} layers={layers} />
      </section>

      <section className="bk-section">
        <h3 className="bk-section-title">Primary backup layers</h3>
        <div className="bk-layer-grid">
          {layers?.map((layer) => (
            <div key={layer.id} className={`bk-layer-card ${STATUS_CLASS[layer.status] || ''}`}>
              <div className="bk-layer-head">
                <span className={`bk-status-pill ${STATUS_CLASS[layer.status]}`}>{layer.status}</span>
                <h4>{layer.name}</h4>
              </div>
              <dl className="bk-layer-dl">
                <div><dt>Device</dt><dd>{layer.device}</dd></div>
                <div><dt>Location</dt><dd className="bk-mono">{layer.location}</dd></div>
                <div><dt>Schedule</dt><dd>{layer.schedule}</dd></div>
                <div><dt>Retention</dt><dd>{layer.retention}</dd></div>
                <div><dt>Size</dt><dd>{layer.sizeHuman}</dd></div>
                <div><dt>Last event</dt><dd>{formatLastEvent(layer)}</dd></div>
              </dl>
              {layer.notes && <p className="bk-layer-note">{layer.notes}</p>}
            </div>
          ))}
        </div>
      </section>

      <section className="bk-section bk-tables-grid">
        <DataTable
          title="Local daily tar backups (Spark)"
          columns={[
            { key: 'name', label: 'File' },
            { key: 'sizeHuman', label: 'Size' },
            { key: 'modifiedAt', label: 'Created', render: (r) => fmtDate(r.modifiedAt) },
            { key: 'path', label: 'Path', mono: true },
          ]}
          rows={tables?.localDailyTars}
        />
        <DataTable
          title="OneDrive — daily tar (nodes-daily)"
          columns={[
            { key: 'name', label: 'File' },
            { key: 'sizeHuman', label: 'Size' },
            { key: 'path', label: 'Cloud path', mono: true },
          ]}
          rows={tables?.oneDriveDaily}
          emptyText={summary?.oneDriveDailyError ? `Not yet uploaded or rclone error: ${summary.oneDriveDailyError}` : 'No daily tar on OneDrive yet — first upload runs after next 1 AM backup'}
        />
        <DataTable
          title="Archive & rotated tars"
          columns={[
            { key: 'name', label: 'File' },
            { key: 'sizeHuman', label: 'Size' },
            { key: 'modifiedAt', label: 'Date', render: (r) => fmtDate(r.modifiedAt) },
          ]}
          rows={tables?.archiveTars}
        />
        <DataTable
          title="Database dumps (archive)"
          columns={[
            { key: 'name', label: 'File' },
            { key: 'sizeHuman', label: 'Size' },
            { key: 'modifiedAt', label: 'Date', render: (r) => fmtDate(r.modifiedAt) },
          ]}
          rows={tables?.databaseDumps}
        />
        <DataTable
          title="Cornelius GFA snapshots"
          columns={[
            { key: 'name', label: 'File' },
            { key: 'sizeHuman', label: 'Size' },
            { key: 'modifiedAt', label: 'Date', render: (r) => fmtDate(r.modifiedAt) },
          ]}
          rows={tables?.corneliusSnapshots}
        />
        <DataTable
          title="Scheduled cron jobs"
          columns={[
            { key: 'label', label: 'Job' },
            { key: 'schedule', label: 'Cron', mono: true },
            { key: 'command', label: 'Command', mono: true },
          ]}
          rows={cronJobs}
        />
        <DataTable
          title="Backup scripts"
          columns={[
            { key: 'name', label: 'Script' },
            { key: 'path', label: 'Path', mono: true },
          ]}
          rows={scripts}
        />
        <DataTable
          title="In-repo ad-hoc backups (sample)"
          className="bk-table-card--wide"
          compact
          maxBodyHeight="420px"
          columns={[
            { key: 'category', label: 'Category', colClass: 'bk-col-category' },
            { key: 'name', label: 'File', colClass: 'bk-col-name' },
            { key: 'sizeHuman', label: 'Size', colClass: 'bk-col-size' },
            {
              key: 'path',
              label: 'Path',
              colClass: 'bk-col-path bk-mono',
              render: (r) => (
                <span className="bk-path-truncate" title={r.path}>{truncatePath(r.path)}</span>
              ),
            },
          ]}
          rows={tables?.adHocBackups}
        />
      </section>

      <section className="bk-section">
        <h3 className="bk-section-title">Recent log lines</h3>
        <div className="bk-log-grid">
          {logs?.map((log) => (
            <div key={log.name} className="bk-log-card">
              <div className="bk-log-head">
                <h4>{log.name}</h4>
                {log.status && (
                  <span className={`bk-status-pill ${STATUS_CLASS[log.status] || 'bk-status-unknown'}`}>
                    {log.status}
                  </span>
                )}
              </div>
              <code className="bk-log-path">{log.path}</code>
              <pre className="bk-log-line">{formatLastEvent(log)}</pre>
            </div>
          ))}
        </div>
      </section>

      <footer className="bk-footer">
        Inventory generated {fmtDate(data.generatedAt)} · {host?.hostname} ({host?.ip})
      </footer>
    </div>
  );
}
